import { Fragment } from "react";
import { Dialog, DialogPanel, DialogTitle, Transition } from "@headlessui/react";
import { classNames } from "../../helpers/classNames";
import Button from "../form/Button";

const Modal = ({
  title,
  subtitle,
  children,
  isOpen = false,
  setIsOpen = () => {},
  size = "xl4",
  modalDialogueStyle = "",
  modalBodyClass = "",
  header,
  closeBtn = true,
  bodyClasses,
  isOutsideClose = true,
  titleClass,
  headerClass,
  drawer = false,
  closeOnEsc = true,
  backdrop = true,
  animation = "scale", // 'scale', 'slide', 'fade'
}) => {
  const modalSize = {
    xs: "sm:max-w-xs",
    sm: "sm:max-w-sm",
    md: "sm:max-w-md",
    lg: "sm:max-w-lg",
    xl: "sm:max-w-xl",
    xl2: "sm:max-w-2xl",
    xl3: "sm:max-w-3xl",
    xl4: "sm:max-w-4xl",
    xl5: "sm:max-w-5xl",
    xl6: "sm:max-w-6xl",
    xl7: "sm:max-w-7xl",
    xl8: "sm:max-w-8xl",
    xl9: "sm:max-w-9xl",
    full: "max-w-full",
  };

  const getAnimationClasses = () => {
    switch (animation) {
      case "slide":
        return {
          enter: "ease-out duration-300",
          enterFrom: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95",
          enterTo: "opacity-100 translate-y-0 sm:scale-100",
          leave: "ease-in duration-200",
          leaveFrom: "opacity-100 translate-y-0 sm:scale-100",
          leaveTo: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95",
        };
      case "fade":
        return {
          enter: "ease-out duration-300",
          enterFrom: "opacity-0",
          enterTo: "opacity-100",
          leave: "ease-in duration-200",
          leaveFrom: "opacity-100",
          leaveTo: "opacity-0",
        };
      default: // scale
        return {
          enter: "ease-out duration-300",
          enterFrom: "opacity-0 scale-95",
          enterTo: "opacity-100 scale-100",
          leave: "ease-in duration-200",
          leaveFrom: "opacity-100 scale-100",
          leaveTo: "opacity-0 scale-95",
        };
    }
  };

  const animationClasses = getAnimationClasses();

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={isOutsideClose ? handleClose : () => {}}
        static={!closeOnEsc}
      >
        {/* Backdrop */}
        {backdrop && (
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          </Transition.Child>
        )}

        {/* Modal Container */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter={animationClasses.enter}
              enterFrom={animationClasses.enterFrom}
              enterTo={animationClasses.enterTo}
              leave={animationClasses.leave}
              leaveFrom={animationClasses.leaveFrom}
              leaveTo={animationClasses.leaveTo}
            >
              <DialogPanel
                className={classNames(
                  "w-full transform overflow-hidden text-left align-middle shadow-2xl transition-all",
                  modalSize[size],
                  modalBodyClass
                )}
              >
                <div
                  className={classNames(
                    "rounded-2xl bg-white relative ring-1 ring-black/5",
                    modalDialogueStyle
                  )}
                >
                  {/* Header */}
                  {header !== false && (
                    <div className="relative flex-shrink-0">
                      {header ? (
                        header
                      ) : (
                        <div
                          className={classNames(
                            "relative py-4 px-6 flex items-center justify-between border-b border-gray-200 bg-primary/30 rounded-t-2xl",
                            headerClass
                          )}
                        >
                          <div className="flex-1 min-w-0">
                            {title && (
                              <DialogTitle
                                as="h3"
                                className={classNames(
                                  "text-lg font-semibold text-gray-900 leading-6",
                                  titleClass
                                )}
                              >
                                {title}
                              </DialogTitle>
                            )}
                            {subtitle && (
                              <p className="mt-1 text-sm text-gray-500 truncate">
                                {subtitle}
                              </p>
                            )}
                          </div>
                          
                          {closeBtn && (
                            <div className="ml-4 flex-shrink-0">
                              <Button
                                buttonIcon={"fa-regular fa-xmark"}
                                buttonIconPosition={"left"}
                                buttonClasses={
                                  "!w-8 !h-8 !p-0 !justify-center !text-lg !bg-transparent !border-0 !text-gray-400 hover:!bg-gray-100 hover:!text-gray-500 !rounded-full transition-all duration-200 hover:scale-110"
                                }
                                buttonFunction={handleClose}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Body */}
                  <div
                    className={classNames(
                      "relative p-6",
                      bodyClasses
                    )}
                  >
                    {children}
                  </div>
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default Modal;